import { nssRoutes } from '../../new-suite-search/nss-routes';
import { ssRoutes } from '../../suite-search/ss-routes';
import { spRoutes } from '../../shipment-portal/routes';
import moment from 'moment/moment';
// @ts-ignore
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { UntypedFormGroup } from '@angular/forms';


const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

export function getRouteKeyMatchByUrlNSS(url: string): string | undefined {
  return nssRoutes.find(r => url.includes(r.route))?.key;
}

export function getRouteKeyMatchByUrlShipmentPortal(url: string): string | undefined {
  return spRoutes.find(r => url.includes(r.route))?.key;
}

export function getRouteKeyMatchByUrlOldSS(url: string): string | undefined {
  return ssRoutes.find(r => url.includes(r.route))?.key;
}

export function downloadCsv(shipments: any): void {
  const replacer = (key: any, value: null) => value === null ? '' : value;
  const header = Object.keys(shipments[0]);
  const csv = shipments.map(( row: { [x: string]: any; }) => header
    .map(fieldName => JSON.stringify(row[fieldName], replacer))
  );
  csv.unshift(header.join(','));
  const csvArray = csv.join('\r\n');

  const blob = new Blob([csvArray], {type: 'text/csv' });
  saveAs(blob, `SuiteSearch_${moment().toISOString()}.csv`);
}

export function downloadXlsx(shipments: any): void {
  const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(shipments);
  const wb: XLSX.WorkBook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `SuiteSearch`);
  XLSX.writeFile(wb, `SuiteSearch_${moment().toISOString()}.xlsx`);
}

export function mapShipmentData(shipment: any): any {

    return {
      ['Customer Name']: shipment.CustomerName,
      ['Pro Num']: shipment.BOL || shipment.ProNum,
      ['Bill of Lading']: shipment.MBL,
      Direction: shipment.DirectionsDesc,
      Carrier: shipment.CarrierName,
      ['Carrier Pro']: shipment.CarrierProNum,
      ['Trans Mode']: shipment.TransMode,
      ['P/U Date']: moment(shipment.PickUpDate).format('MM/DD/YYYY'),
      ['Delivery Date']: moment(shipment.DropOffDate).format('MM/DD/YYYY'),
      Ver: shipment.CarrierInvVersion,
      Shipper: shipment.ShipperName,
      ['Origin Address']: shipment.ShipperAddr ? `${shipment.ShipperAddr}, ${shipment.ShipperCity} - ${shipment.ShipperState}, ${shipment.ShipperZip}` : '',
      Consignee: shipment.ConsigneeName,
      ['Consignee Address']: shipment.ConsigneeAddr ? `${shipment.ConsigneeAddr}, ${shipment.ConsigneeCity} - ${shipment.ConsigneeState}, ${shipment.ConsigneeZip}` : '',
      ['Purchase Order']: shipment?.RefNumber.find(( r: { RefNumberDesc: string; }) => r.RefNumberDesc === 'PO Number')?.RefValue,
      ['Expense Code']: shipment.ExpenseCodeDescription,
      ['Invoice Date']: shipment.CustomerInvoiceDate ? moment(shipment.CustomerInvoiceDate).format('MM/DD/YYYY') : '',
      ['Billing Status']: shipment.BillingStatus,
      ['Shipment Status']: shipment.DeliverStatus?.trim(),
      ['Freight Charge']:  currencyFormatter.format(shipment.FreightCharges || 0.00),
      ['PreProgram Charge']: currencyFormatter.format(shipment.PreProgramCharge || 0.00),
      ['Logistics Savings']: currencyFormatter.format(shipment.LogSavings || 0.00),
      ['Cutback Amount']: currencyFormatter.format(shipment.CutbackAmount || 0.00),
      ['Trans Fee']: currencyFormatter.format(shipment?.Charges.find(( c: { ChargeId: number; }) => c.ChargeId === 347)?.Amount || 0.00),
      ['Fuel Charge']: currencyFormatter.format(shipment?.Charges.find(( c: { ChargeId: number; }) => c.ChargeId === 7)?.Amount || 0.00),
      ['Linehaul Charge']: currencyFormatter.format(shipment?.Charges
        .find(( c: { ChargeId: number; }) => c.ChargeId === 5)?.Amount || 0.00),
      ['Total Customer Charge']: currencyFormatter.format(shipment.TotalCustomerCharge || 0.00),
      ['Processing Fee']: currencyFormatter.format(shipment?.Charges
        .find(( c: { ChargeId: number; }) => c.ChargeId === 348)?.Amount || 0.00),
      ['Gain Share']: currencyFormatter.format(shipment?.Charges.find(( c: { ChargeId: number; }) => c.ChargeId === 349)?.Amount || 0.00),
      ['Lost Savings']: currencyFormatter.format(shipment.LostSavings || 0.00)
    };
}

export function buildFilters(searchForm: UntypedFormGroup, checklistSelection: any): string {
  let filter = '';

  Object.keys(searchForm.controls).forEach(key => {
    if (searchForm.get(key)?.value) {
      switch ( key ) {
        case 'CarrierProNum':
          filter += buildQueryParameters(key, searchForm);
          filter = removeTrailingAndOr(filter, 'and', 'or');
          break;
        case 'CarrierInvoiceNum':
          filter += buildQueryParameters(key, searchForm);
          filter = removeTrailingAndOr(filter, 'and', 'or');
          break;
        case 'MBL':
          filter += buildQueryParameters(key, searchForm);
          filter = removeTrailingAndOr(filter, 'and', 'or');
          break;
        case 'BOL':
        case 'ProNum':
          filter += buildQueryParameters(key, searchForm);
          filter = removeTrailingAndOr(filter, 'and', 'or');
          break;
        case 'EndDropOffDate':
          const startDropOffDate = moment(searchForm.get('StartDropOffDate')?.value).format('YYYY-MM-DD');
          const endDropOffDate = moment(searchForm.get('EndDropOffDate')?.value).format('YYYY-MM-DD');
          filter += ' and (';
          filter += `DropOffDate ge ${startDropOffDate}T00:00:00.000Z and DropOffDate le ${endDropOffDate}T23:59:59.000Z`;
          filter = removeTrailingAndOr(filter, 'and', 'or');
          filter += ')';
          break;
        case 'EndPickUpDate':
          const startPickUpDate = moment(searchForm.get('StartPickUpDate')?.value).format('YYYY-MM-DD');
          const endPickUpDate = moment(searchForm.get('EndPickUpDate')?.value).format('YYYY-MM-DD');
          filter += ' and (';
          filter += `PickUpDate ge ${startPickUpDate}T00:00:00.000Z and PickUpDate le ${endPickUpDate}T23:59:59.000Z`;
          filter = removeTrailingAndOr(filter, 'and', 'or');
          filter += ')';
          break;
        case 'CarrierName':
          filter += ' and (';
          filter += `search.ismatch('${searchForm.get(key)?.value}', 'CarrierName')`;
          filter = removeTrailingAndOr(filter, 'and', 'or');
          filter += ')';
          break;
        case 'CUSTID':
          filter += ' and (';
          filter += `search.ismatch('${checklistSelection.selected.map(( customer: { custID: any; }) => customer.custID).join(',')}', 'CUSTID')`;
          filter = removeTrailingAndOr(filter, 'and', 'or');
          filter += ')';
          break;
        case 'EndCustomerInvoiceDate':
          const startCustomerInvoiceDate = moment(searchForm.get('StartCustomerInvoiceDate')?.value).format('YYYY-MM-DD');
          const endCustomerInvoiceDate = moment(searchForm.get('EndCustomerInvoiceDate')?.value).format('YYYY-MM-DD');
          filter += ' and (';
          filter += `CustomerInvoiceDate ge ${startCustomerInvoiceDate}T00:00:00.000Z and CustomerInvoiceDate le ${endCustomerInvoiceDate}T23:59:59.000Z`;
          filter = removeTrailingAndOr(filter, 'and', 'or');
          filter += ')';
          break;
        case 'EndCheckDate':
          const startCheckDate = moment(searchForm.get('StartCheckDate')?.value).format('YYYY-MM-DD');
          const endCheckDate = moment(searchForm.get('EndCheckDate')?.value).format('YYYY-MM-DD');
          filter += ' and (';
          filter += `CheckDate ge ${startCheckDate}T00:00:00.000Z and CheckDate le ${endCheckDate}T23:59:59.000Z`;
          filter = removeTrailingAndOr(filter, 'and', 'or');
          filter += ')';
          break;
        case 'CheckNumber':
          filter += ' and (';
          searchForm.get(key)?.value.split(',').forEach(( checkNum: any) => {
            filter += `${key} eq '${checkNum}' or `;
          });
          filter = removeTrailingAndOr(filter, 'and', 'or');
          filter += ')';
          break;
        default:
          break;
      }
    }
  });
  return filter;
}

function buildQueryParameters(key: string, searchForm: UntypedFormGroup): string {
  let filter = '';

  filter += ' and (';
  filter += `search.in(${key}, '${searchForm.get(key)?.value}')`;
  filter += ')';

  return filter;
}

function removeTrailingAndOr(filterQuery: string, endsWith: string, orWith: string): string {
  if (filterQuery.endsWith(` ${endsWith} `) || filterQuery.endsWith(` ${orWith} `)) {
    filterQuery = filterQuery.slice(0, -4);
  }
  if (filterQuery.startsWith(` ${endsWith} `) || filterQuery.startsWith(` ${orWith} `)) {
    filterQuery = filterQuery.slice(5);
  }
  return filterQuery;
}

